import type { EntityState } from "@reduxjs/toolkit";
import { generate } from "short-uuid";

import { ArgumentType } from "#/entities/argument/model/argumentObject";
import {
  type ArrayBaseType,
  makeArrayBaseClass,
} from "#/entities/dataStructures/array/model/arrayBase";
import {
  arrayDataItemSelectors,
  type ArrayItemData,
} from "#/entities/dataStructures/array/model/arraySlice";
import type { CallstackHelper } from "#/features/callstack/model/callstackSlice";

import { generateArrayData } from "../lib/generateArrayData";

const ArrayBase = makeArrayBaseClass(Array);

export type ControlledArrayRuntimeOptions = {
  length?: number;
  colorMap?: Record<string, string>;
  /** User source variable name (inferred by AST); shown as a viewer label, not the internal tree id. */
  displayLabel?: string;
};

const RUNTIME_DISPLAY_LABEL_KEY = "__dstructRuntimeDisplayLabel";

const isDisplayLabelRuntimeOptions = (
  value: unknown,
): value is ControlledArrayRuntimeOptions => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (!("displayLabel" in record)) return false;
  if (typeof record.displayLabel !== "string") return false;
  return Object.keys(record).length === 1;
};

const getRuntimeDisplayLabel = (array: ArrayBaseType): string | undefined => {
  const label = Reflect.get(array, RUNTIME_DISPLAY_LABEL_KEY);
  return typeof label === "string" ? label : undefined;
};

const assertArrayProxyElementsAreValid = (
  elements: ReadonlyArray<unknown>,
): void => {
  for (const element of elements) {
    if (element === undefined) continue;
    if (typeof element === "number" || typeof element === "string") continue;
    throw new Error("ArrayProxy can only contain numbers or strings");
  }
};

export function initControlledArray<T extends ArrayBaseType>(
  array: T,
  arrayData: EntityState<ArrayItemData, string>,
  name: string,
  callstack: CallstackHelper,
  addToCallstack?: boolean,
  options?: ControlledArrayRuntimeOptions,
  argType: ArgumentType = ArgumentType.ARRAY,
) {
  Object.defineProperties(array, {
    name: {
      value: name,
      enumerable: false,
    },
    structureType: {
      value: "array",
      enumerable: false,
    },
    itemsMeta: {
      value: arrayDataItemSelectors.selectAll(arrayData),
      enumerable: false,
    },
    argType: {
      value: argType,
      enumerable: false,
    },
    callstack: {
      value: callstack,
      enumerable: false,
    },
    ...(options?.displayLabel !== undefined
      ? {
          [RUNTIME_DISPLAY_LABEL_KEY]: {
            value: options.displayLabel,
            enumerable: false,
          },
        }
      : {}),
  });

  if (addToCallstack) {
    array.callstack.addOne({
      ...array.getDispatchBase(),
      name: "addArray",
      args: { arrayData, options },
    });
  }

  return new Proxy(array, {
    get: (target, prop, receiver) => {
      const index = typeof prop === "string" ? Number(prop) : -1;
      if (
        globalThis.recordReads !== false &&
        Number.isInteger(index) &&
        index >= 0
      ) {
        array.blink(index);
      }

      const value = Reflect.get(target, prop, receiver);
      if (typeof value === "function" && prop === "toString") {
        return value.bind(target);
      }
      return value;
    },
    set: (target, prop, value) => {
      const isSuccessful = Reflect.set(target, prop, value);
      if (isSuccessful) {
        const index = typeof prop === "string" ? Number(prop) : -1;
        if (Number.isInteger(index) && index >= 0) {
          array.updateItem(value, index);
        }
      }

      return isSuccessful;
    },
  });
}

export class ControlledArray<T> extends ArrayBase<T> {
  private readonly itemsMeta!: ArrayItemData[];

  constructor(
    array: Array<T>,
    name: string,
    arrayData: EntityState<ArrayItemData, string>,
    callstack: CallstackHelper,
    addToCallstack?: boolean,
    options?: ControlledArrayRuntimeOptions,
  ) {
    super();
    this.push(...array);

    return initControlledArray(
      this,
      arrayData,
      name,
      callstack,
      addToCallstack,
      options,
    );
  }

  static _from(
    callstack: CallstackHelper,
    inputArray: Array<number | string>,
    mapFn?: (item: number | string | undefined, index: number) => unknown,
    _?: unknown,
    options?: ControlledArrayRuntimeOptions,
  ) {
    const { id, array, data } = ControlledArray._mapArrayData(
      new Array(inputArray.length),
    );
    const newArray = new ControlledArray(
      array,
      id,
      data,
      callstack,
      true,
      options,
    );

    for (let i = 0; i < inputArray.length; i++) {
      newArray[i] = mapFn ? mapFn(inputArray[i], i) : inputArray[i];
    }

    return newArray;
  }

  static _mapArrayData<T, U>(
    inputArray: T[],
    mapFn?: (item: T, index: number, array: T[]) => U,
  ) {
    const array = [];
    if (mapFn) {
      for (let i = 0; i < inputArray.length; i++) {
        array[i] = mapFn(inputArray[i] as T, i, inputArray);
      }
    } else {
      array.push(...inputArray);
    }
    const data = generateArrayData(array);
    const id = generate();

    return {
      id,
      array,
      data,
    };
  }

  override pop() {
    const base = this.getDispatchBase(this.length - 1);
    const value = super.pop();
    this.callstack.addOne({
      ...base,
      name: "deleteNode",
    });
    this.itemsMeta.pop();
    return value;
  }

  override map<U>(
    callback: (value: T, index: number, array: T[]) => U,
    _?: unknown,
    options?: ControlledArrayRuntimeOptions,
  ): ControlledArray<U> {
    const { id, array, data } = ControlledArray._mapArrayData(
      new Array(this.length),
      undefined,
    );
    const inheritedDisplayLabel = getRuntimeDisplayLabel(this);
    const mapOptions: ControlledArrayRuntimeOptions | undefined =
      inheritedDisplayLabel !== undefined || options !== undefined
        ? {
            ...options,
            displayLabel: options?.displayLabel ?? inheritedDisplayLabel,
          }
        : undefined;
    const newArray = new ControlledArray(
      array as U[],
      id,
      data,
      this.callstack,
      true,
      mapOptions,
    );
    for (let i = 0; i < this.length; i++) {
      newArray[i] = callback(this[i]!, i, this);
    }
    return newArray;
  }

  override slice(start?: number, end?: number): T[] {
    const slicedArray = Array.from(this).slice(start, end);
    const { id, array, data } = ControlledArray._mapArrayData(slicedArray);
    const inheritedDisplayLabel = getRuntimeDisplayLabel(this);
    const sliceOptions: ControlledArrayRuntimeOptions | undefined =
      inheritedDisplayLabel !== undefined
        ? { displayLabel: inheritedDisplayLabel }
        : undefined;
    return new ControlledArray(
      array as T[],
      id,
      data,
      this.callstack,
      true,
      sliceOptions,
    );
  }

  protected getNodeMeta(key: number): ArrayItemData | undefined {
    return this.itemsMeta.at(key);
  }

  protected setNodeMeta(key: any, data: ArrayItemData): void {
    this.itemsMeta[key] = data;
  }

  public setHeaders(rowHeaders: string[], colHeaders: string[]) {
    this.callstack.addOne({
      ...this.getDispatchBase(),
      name: "setHeaders",
      args: { rowHeaders, colHeaders },
    });
  }

  public setHeaderRanges({
    rowStart,
    rowEnd,
    colStart,
    colEnd,
  }: {
    rowStart: number;
    rowEnd: number;
    colStart: number;
    colEnd: number;
  }) {
    const rowHeaders = Array.from({ length: rowEnd - rowStart }, (_, index) =>
      String(index + rowStart),
    );
    const colHeaders = Array.from({ length: colEnd - colStart }, (_, index) =>
      String(index + colStart),
    );

    this.setHeaders(rowHeaders, colHeaders);
  }

  public showIndexes(rowCount: number, colCount: number) {
    if (rowCount === undefined || colCount === undefined) {
      const row = this[0];
      let cols = this.length;
      if (Array.isArray(row)) {
        cols = row.length;
      }
      this.setHeaderRanges({
        rowStart: 0,
        rowEnd: this.length,
        colStart: 0,
        colEnd: cols,
      });
    } else {
      this.setHeaderRanges({
        rowStart: 0,
        rowEnd: rowCount,
        colStart: 0,
        colEnd: colCount,
      });
    }
  }
}

export const getRuntimeArrayClass = (callstack: CallstackHelper) =>
  class ArrayProxy<T extends string | number> extends ControlledArray<T> {
    constructor(arrayLength: number);
    constructor(...items: Array<T>) {
      let runtimeOptions: ControlledArrayRuntimeOptions | undefined;
      let elementItems = items as Array<T | number>;
      const lastItem = elementItems.at(-1);
      if (isDisplayLabelRuntimeOptions(lastItem)) {
        runtimeOptions = lastItem;
        elementItems = elementItems.slice(0, -1) as Array<T | number>;
      }

      if (elementItems.length === 1 && typeof elementItems[0] === "number") {
        const arrayLength = elementItems[0];
        elementItems = new Array(arrayLength) as Array<T | number>;
      }

      assertArrayProxyElementsAreValid(elementItems);

      const data = generateArrayData(elementItems as T[]);

      super(
        elementItems as T[],
        generate(),
        data,
        callstack,
        true,
        runtimeOptions,
      );
    }

    static override from(
      array: Array<number | string>,
      mapFn?: (
        item: number | string | undefined,
        index: number,
      ) => number | string,
      thisArg?: unknown,
      options?: ControlledArrayRuntimeOptions,
    ) {
      return ControlledArray._from(callstack, array, mapFn, thisArg, options);
    }
  };
